import React, { useState, useEffect } from 'react';
import { X, Clock, Users, Minus, Plus, ChefHat, Share2, Printer, Check, Edit2, Heart, PoundSterling, ChevronDown, Utensils, createLucideIcon } from 'lucide-react';
import { Recipe, useStore } from '../../store/useStore';
import { getWeekInfo } from '../../lib/weekHelper';
import { toFractionHTML, parseIngredient } from '../../lib/ingredientParser';
import { cuisineFlags, cuisineIcons, tagColor } from '../../lib/tagsManager';
import { ChevronRight } from "lucide-react"; // or use an inline SVG if preferred
import { ChefsNotes } from './ChefsNote';
import { pepperChilli } from '@lucide/lab';

interface RecipeModalProps {
  recipe: Recipe;
  onClose: () => void;
}

function decodeHtmlEntities(str: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return doc.documentElement.textContent || '';
}

const RecipeModal: React.FC<RecipeModalProps> = ({ recipe, onClose }) => {
  const [activeTab, setActiveTab] = useState<'ingredients' | 'instructions'>('ingredients');
  const [savedRecipes, toggleFavorite, addNotification] = useStore(
    (state) => [state.savedRecipes, state.toggleFavorite, state.addNotification]
  );
  const [servings, setServings] = useState(recipe.servings);
  const user = useStore((state) => state.user);
  const location = useStore((state) => state.location);
  const estimateRecipeCost = useStore((state) => state.estimateRecipeCost);
  const [showAddToPlanModal, setShowAddToPlanModal] = useState(false);
  const weeklyPlan = useStore((state) => state.weeklyPlan);
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner'>('breakfast');
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isEditingCost, setIsEditingCost] = useState(false);
  const [customCost, setCustomCost] = useState(() => {
    const cost =
      user?.estimatedCosts && user?.id && user?.estimatedCosts[recipe.id]
        ? user?.estimatedCosts[recipe.id].amount :
        recipe.estimatedCosts && user?.id && recipe?.estimatedCosts[user.id]
          ? recipe.estimatedCosts[user.id].amount : recipe.estimatedCost || 0;
    return Number(cost)?.toFixed(2);
  });
  const [scaledIngredients, setScaledIngredients] = useState<any[]>(recipe.ingredients || []);
  const [isOpen, setIsOpen] = useState(true);

  const getMealCount = (type: string) => {
    let weekId = getWeekInfo();
    return weeklyPlan[weekId].reduce((count, day) => {
      return count + (day.meals[type as 'breakfast' | 'lunch' | 'dinner' | 'snacks'] ? 1 : 0);
    }, 0);
  };

  const PepperChilli = createLucideIcon("PepperChilli", pepperChilli);

  useEffect(() => {
    const customCost = user?.estimatedCosts && user?.id && user?.estimatedCosts[recipe.id]
      ? user?.estimatedCosts[recipe.id].amount :
      recipe.estimatedCosts && user?.id && recipe?.estimatedCosts[user.id]
        ? recipe.estimatedCosts[user.id].amount : recipe.estimatedCost || 0;
    setCustomCost(Number(customCost)?.toFixed(2));
  }, [user])

  const handleCostUpdate = () => {
    let currency = location.currency || "£";
    try {
      estimateRecipeCost(recipe.id, currency, Number(customCost) ?? 0);
    } catch (err) {
      console.log("Error: ", err);
    }
    setIsEditingCost(false);

    addNotification({
      title: 'Cost Updated',
      message: `Cost estimate for ${recipe.title} has been updated`,
      type: 'success'
    });
  };


  const scaleIngredient = (ingredient: string, scale: number, key: number): string => {
    // const numberRegex = /^(\d*\.?\d+)\s*/;
    // const match = ingredient.match(numberRegex);
    const numberRegex = /(\d*\.?\d+)\s*/;

    const match = ingredient.match(numberRegex);

    // console.log("Scale: ", match, scale, key);
    // console.log("Original ingredient: ", match, scale, key);

    if (match) {
      const originalAmount = parseFloat(match[1]);
      const scaledAmount = Number((originalAmount * scale).toFixed(3));
      const rounded = Math.round(scaledAmount * 4);
      const scaledAmountNum = (rounded / 4).toFixed(2);
      const formattedAmount = scaledAmountNum.endsWith('.00')
        ? scaledAmountNum.slice(0, -3)
        : scaledAmountNum;
      ingredient = ingredient.replace(numberRegex, `${formattedAmount} `);
      // console.log("New ingredient: ", ingredient, originalAmount, formattedAmount, numberRegex);
    }

    ingredient = ingredient.replace(/(\d+)\s+(tbsp|g|tsp|spoon|ml|oz|cups|cup|l|lb)\b/gi, "$1$2");
    ingredient = ingredient.replace(/\s*-\s*$/, '').trim();

    // console.log(ingredient);

    return ingredient;
  };

  const formattedIngredient = (rawIngredient: string) => {
    const { quantity, unit, item, matchType, metric, altQuantity, itemRaw } = parseIngredient(rawIngredient);
    return { quantity, unit, item, matchType, metric, altQuantity, quantityFraction: toFractionHTML(altQuantity), itemRaw };
  }

  const handleServingsChange = (servings: number, change: number) => {
    const newServings = Math.max(1, servings + change);
    // const actualServing = servings + change;
    // console.log(servings, change, actualServing);
    const scale = newServings / recipe.servings;
    // console.log("Scale: ", scale, recipe.ingredients);
    const scaledIngredients = recipe.ingredients?.map((ingredient, index) => scaleIngredient(ingredient, scale, index));
    setScaledIngredients(scaledIngredients || []);
    setServings(newServings);
  };

  // const getScaledIngredients = () => {
  //   const scale = servings / recipe.servings;
  //   console.log("Scale: ", scale, recipe.ingredients);
  //   let ingredients = recipe.ingredients?.map(ingredient => scaleIngredient(ingredient, scale));
  //   setScaledIngredients(ingredients || []);
  //   return recipe.ingredients?.map(ingredient => scaleIngredient(ingredient, scale));
  // };

  const getScaledTime = () => {
    const scale = servings / recipe.servings;
    // const prepTime = scaleTime(recipe.prepTime, scale);
    // const cookTime = scaleTime(recipe.cookTime, scale);

    function scaleTextTime(input: string, scaleFactor: number) {
      return input.replace(/(\d+)\s*-\s*(\d+)|\d+/g, (match, p1, p2) => {
        if (p1 && p2) {
          // Range match
          const val1 = parseInt(p1, 10) * scaleFactor;
          const val2 = parseInt(p2, 10) * scaleFactor;

          if (val1 >= 60 && val2 >= 60) {
            // Convert to hours range with floor/ceil
            const h1 = Math.floor(val1 / 60);
            const h2 = Math.ceil(val2 / 60);
            return `${h1} to ${h2} hr${h2 !== 1 ? 's' : ''}`;
          } else {
            return `${val1} - ${val2}`;
          }
        } else {
          // Single value
          const value = parseInt(match, 10) * scaleFactor;
          return formatTime(value);
        }
      });
    }

    function formatTime(minutes: number) {
      if (minutes >= 60) {
        const hours = minutes / 60;
        return hours % 1 === 0 ? `${hours} hr${hours !== 1 ? 's' : ''}` : `${hours.toFixed(1)} hrs`;
      } else {
        return `${minutes} min${minutes !== 1 ? 's' : ''}`;
      }
    }

    // ✅ Example usage
    const texts = [
      "under 15 minute",
      "5 - 10 minute",
      "less than 30 minutes",
      "more than 60 minutes",
      "between 12 - 20 minute",
      "about 90 minutes"
    ];

    const scaleFactor = scale;

    // console.log(JSON.stringify(results));
    return {
      prep: recipe.prepTime ? scaleTextTime(String(recipe.prepTime), scaleFactor) : "",
      cook: recipe.cookTime ? scaleTextTime(String(recipe.cookTime), scaleFactor) : "",
      total: recipe.cookTime ? scaleTextTime(String(recipe.cookTime), scaleFactor) : ""
    };
  };

  const handleSave = () => {
    toggleFavorite(recipe.id);
    // addNotification({
    //   title: savedRecipes.includes(recipe.id) ? 'Recipe removed' : 'Recipe saved',
    //   message: savedRecipes.includes(recipe.id)
    //     ? `${recipe.title} has been removed from your saved recipes`
    //     : `${recipe.title} has been added to your saved recipes`,
    //   type: 'info'
    // });
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: recipe.title,
        text: `Check out this recipe for ${recipe.title}!`,
        url: window.location.href
      });
      addNotification({
        title: 'Recipe shared',
        message: `${recipe.title} has been shared successfully`,
        type: 'success'
      });
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        addNotification({
          title: 'Share failed',
          message: 'Unable to share the recipe at this time',
          type: 'error'
        });
      }
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const scaledTime = getScaledTime();

    const content = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${recipe.title} Recipe</title>
        <style>
          /* --------- Layout Basics ---------- */
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Source+Sans+Pro:wght@400;600&display=swap');

          body {
            font-family: 'Source Sans Pro', system-ui, sans-serif;
            background: #faf9f7;
            color: #333;
            margin: 0;
            padding: 0;
            line-height: 1.7;
          }
          h1, h2 {
            font-family: 'Playfair Display', serif;
            font-weight: 600;
            color: #222;
            margin: 0 0 0.5rem 0;
          }
          .hero {
            position: relative;
            width: 100%;
            height: 320px;
            overflow: hidden;
          }
          .hero img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            filter: brightness(90%);
          }
          .hero-title {
            position: absolute;
            bottom: 20px;
            left: 40px;
            color: white;
            font-size: 3rem;
            text-shadow: 0 4px 10px rgba(0,0,0,0.4);
          }

          /* ---------- Meta Section ---------- */
          .meta {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            background: #fff;
            padding: 1.2rem;
            margin: 0 auto;
            gap: 1.5rem;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
          }
          .meta-item {
            text-align: center;
            font-size: 0.95rem;
          }
          .meta-item strong {
            display: block;
            font-weight: 600;
            font-size: 1.05rem;
            color: #1e293b;
          }

          /* ---------- Sections ---------- */
          .section {
            max-width: 900px;
            margin: 2.5rem auto;
            padding: 0 2rem;
          }
          .ingredients ul {
            columns: 2;
            column-gap: 2rem;
            list-style: square;
            padding-left: 1.2rem;
            margin: 1rem 0 0;
          }
          .instructions {
            counter-reset: step;
          }
          .step {
            counter-increment: step;
            margin: 0 0 1rem 0;
            padding-left: 2.5rem;
            position: relative;
          }
          .step::before {
            content: counter(step);
            position: absolute;
            left: 0;
            top: 0;
            background: #3AA0A6; /* #e11d48; */
            color: white;
            width: 1.8rem;
            height: 1.8rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Playfair Display', serif;
            font-weight: 600;
          }
          .notes {
            background: #fff8e1;
            border-left: 5px solid #fbbf24;
            padding: 1rem 1.5rem;
            margin-top: 2rem;
            font-style: italic;
          }

          /* ---------- Print tweaks ---------- */
          @media print {
            body {
              background: white;
            }
            .hero img {
              filter: brightness(100%);
            }
          }
        </style>
      </head>
      <body>

        <div class="hero">
          ${recipe.image ? `<img src="${recipe.image}" alt="${recipe.title}" />` : ""}
          <div class="hero-title">${recipe.title}</div>
        </div>

        <div class="meta">
          <div class="meta-item"><strong>Prep</strong>${recipe.prepTime}</div>
          <div class="meta-item"><strong>Cook</strong>${recipe.cookTime}</div>
          <div class="meta-item"><strong>Servings</strong>${servings}</div>
          <div class="meta-item"><strong>Difficulty</strong>${recipe.difficulty}</div>
          <div class="meta-item"><strong>Cost</strong>£${customCost ?? 0}</div>
        </div>

        <div class="section ingredients">
          <h2>Ingredients</h2>
          <ul>
            ${scaledIngredients?.map(i => `<li>${i}</li>`).join("")}
          </ul>
        </div>

        <div class="section instructions">
          <h2>Instructions</h2>
          ${recipe.instructions?.map(step => `<p class="step">${step}</p>`).join("")}
        </div>

        ${recipe.notes ? `
          <div class="section notes">
            <h2>Chef’s Notes (Recipe by Ezyvibes)</h2>
            <p>${recipe.notes}</p>
          </div>` : ""}
      </body>
    </html>
  `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };


  const toggleDateSelection = (date: Date) => {
    const dateString = date.toDateString();
    const isSelected = selectedDates.some(d => d.toDateString() === dateString);

    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => d.toDateString() !== dateString));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const handleAddToPlan = () => {
    // let weekId = getWeekInfo();
    // selectedDates.forEach(date => {
    //   addMealToPlan(date, selectedMealType, weekId, recipe as Meal);
    // });

    addNotification({
      title: 'Recipe added to plan',
      message: `${recipe.title} has been added to your meal plan`,
      type: 'success'
    });

    setSelectedDates([]);
    setShowAddToPlanModal(false);
  };

  const scaledTime = getScaledTime();

  const tags = recipe.tags || [];
  const category = recipe.category || [];

  const mealTypes = ["Breakfast", "Lunch", "Dinner"];

  const baseServings = Number(recipe.servings);

  let meals = [];
  let cuisines = [];
  let cleanTags = [];

  for (const item of category) {
    const cleanCategory = decodeHtmlEntities(item);
    if (mealTypes.includes(item)) {
      meals.push(cleanCategory);
    } else {
      cuisines.push(cleanCategory);
    }
  }

  if (tags) {
    for (const item of tags) {
      const cleanTag = decodeHtmlEntities(item);
      cleanTags.push(cleanTag);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 modal-container">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] safari:max-h-[calc(100vh-4rem)] overflow-hidden">
        <div className="flex h-full">
          {/* Left Column - Image and Quick Actions */}
          <div className="w-1/3 relative hidden lg:block">
            <div className="absolute inset-0">
              <img
                src={recipe.image}
                alt={recipe.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ChefHat className="text-secondary" size={20} />
                  <span className="text-sm">Recipe by Ezyvibes</span>
                </div>

                <div className="text-white text-sm font-medium flex flex-col justify-center z-10 pointer-events-none space-y-2">
                  <div className="flex flex-wrap gap-2 max-w-xs">
                    <span className="flex items-center px-2 py-1 rounded-full bg-gray-300 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300">
                      <Utensils size={12} className='mr-1' /> {meals[0]}
                    </span>
                    {cuisines.map((cuisine, index) => (
                      <span
                        key={index}
                        className={`flex items-center px-2 py-1 rounded-full bg-gray-300 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300`}
                      >
                        {/* {cuisineIcons[cuisine] || cuisineIcons.Default} */}
                        {/* {cuisineFlags[cuisine] || cuisineFlags.Default} */}
                        {/* &nbsp; */}
                        {cuisine}
                      </span>
                    ))}

                    {cleanTags.map((tag, index) => (
                      <span
                        key={index}
                        className={`flex items-center px-2 py-1 rounded-full ${tagColor(tag)} text-gray-800 dark:text-gray-200`}
                      >
                        {cuisineIcons[tag] || cuisineIcons.Default}
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  {/* <button
                    onClick={() => setShowAddToPlanModal(true)}
                    disabled
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Calendar size={18} />
                  </button> */}
                  <button
                    onClick={handleSave}
                    className={`p-2 rounded-lg transition-colors ${savedRecipes.includes(recipe.id)
                      ? 'bg-secondary text-white'
                      : 'bg-white/10 hover:bg-white/20'
                      }`}
                  >
                    <Heart size={18} fill={recipe.isFavorite ? 'red' : 'none'} />
                    {/* <Bookmark size={18} fill={savedRecipes.includes(recipe.id) ? 'currentColor' : 'none'} /> */}
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Share2 size={18} />
                  </button>
                  <button
                    onClick={handlePrint}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Printer size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Recipe Content */}
          <div className="flex-1 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>

              {/* Toggle Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex justify-between items-center text-left gap-2"
              >
                <h2 className={`text-2xl font-bold text-gray-800 dark:text-white ${isOpen ? 'mb-2' : ''}`}>
                  {recipe.title}
                </h2>
                <ChevronDown
                  width={12}
                  height={12}
                  className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                />
              </button>

              {isOpen && <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-400">
                <div>
                  <span>{recipe.description}</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center">
                      <Clock size={18} className="mr-2 text-secondary" />
                      <div className="text-sm">
                        <span className="block text-gray-900 dark:text-white font-medium">
                          {/* {scaledTime.total.replace("mins mins", "mins").replace("hrs mins", "hrs")} */}
                          {recipe.cookTime}
                        </span>
                        {/* {scaledTime.prep && <span className="text-xs">
                          {scaledTime.prep}
                        </span>} */}
                        {recipe.prepTime && <span className="text-xs">
                          {recipe.prepTime} (prep)
                        </span>}
                      </div>
                    </div>

                    {recipe.spiceLevel && <div className="flex items-center">
                      <PepperChilli size={18} className="mr-2 text-red-300" />
                      <span>{recipe.spiceLevel}</span>
                    </div>}

                    <div className="flex items-center">
                      <Users size={18} className="mr-2 text-secondary" />
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleServingsChange(servings, (baseServings * -1))}
                          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                          disabled={servings <= baseServings}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {servings}
                        </span>
                        <button
                          onClick={() => handleServingsChange(servings, (baseServings *1))}
                          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${recipe.difficulty === 'Easy'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : recipe.difficulty === 'Medium'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                    {recipe.difficulty}
                  </span>

                  <div className="flex items-center">
                    <PoundSterling size={18} className="mr-1 text-secondary" />
                    {isEditingCost ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={customCost}
                          onChange={(e) => setCustomCost(parseFloat(e.target.value).toFixed(2))}
                          className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          step="0.01"
                          min="0"
                        />
                        <button
                          onClick={handleCostUpdate}
                          className="p-1 text-secondary hover:text-secondary/80"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 dark:text-white">
                          {customCost || '0.00'}
                        </span>
                        <button
                          onClick={() => setIsEditingCost(true)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('ingredients')}
                  className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'ingredients'
                    ? 'border-secondary text-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Ingredients
                </button>
                <button
                  onClick={() => setActiveTab('instructions')}
                  className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'instructions'
                    ? 'border-secondary text-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Instructions
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 modal-content flex flex-col h-108 overflow-hidden">
              <div className="flex-1 overflow-y-auto pr-2">
                {activeTab === 'ingredients' ? (
                  <ul className="space-y-3">
                    {scaledIngredients?.map((ing, index) => (
                      <li key={index} className='flex items-center text-gray-700 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors'>
                        <span className={`${formattedIngredient(ing).metric ? "" : "gap-2"} flex`}>
                          <div>
                            {formattedIngredient(ing).altQuantity > 0 && <span dangerouslySetInnerHTML={{ __html: formattedIngredient(ing).quantityFraction }}></span>}
                            {formattedIngredient(ing).metric && (
                              <>
                                {["tbsp", "tsp", "tins", "tin", "slice", "slices", "cup", "cups"].includes(formattedIngredient(ing).metric) ? (
                                  <span className="ml-1">{formattedIngredient(ing).metric}</span>
                                ) : (
                                  formattedIngredient(ing).metric
                                )}{" "}
                                of
                              </>
                            )}{" "}
                            {formattedIngredient(ing).itemRaw}
                          </div>

                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ol className="space-y-6">
                    {recipe.instructions?.map((instruction, index) => (
                      <li
                        key={index}
                        className="flex gap-4 text-gray-700 dark:text-gray-300"
                      >
                        <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10 text-secondary font-medium">
                          {index + 1}
                        </span>
                        <p className="pt-1">{instruction}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {recipe.notes && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl shrink-0">
                  <ChefsNotes notes={recipe.notes} />
                  <div className="relative max-w-xl mt-4">
                    <button
                      onClick={() => window.open('https://ezyvibes.com/disclaimer', '_blank')}
                      className="flex items-center justify-between w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-900 px-4 py-2 rounded-lg mb-2 transition-colors duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <span className="truncate font-medium">
                        ⚠️ Allergy Notice:
                        <span className="hidden sm:inline"> Recipes may contain common allergens.</span>
                      </span>
                      <span className="flex items-center gap-1 text-sm text-yellow-800 group-hover:text-yellow-900 transition-colors">
                        Learn More <ChevronRight className="w-4 h-4" />
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {showAddToPlanModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Add to Meal Plan
                </h3>
                <button
                  onClick={() => {
                    setShowAddToPlanModal(false);
                    setSelectedDates([]);
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex justify-center space-x-4 mb-6">
                {['breakfast', 'lunch', 'dinner'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedMealType(type as 'breakfast' | 'lunch' | 'dinner')}
                    className={`px-4 py-2 rounded-lg flex flex-col items-center ${selectedMealType === type
                      ? 'bg-secondary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <span className="capitalize">{type}</span>
                    <span className="text-xs mt-1">
                      {getMealCount(type)}/7 meals
                    </span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 7 }, (_, i) => {
                  const date = new Date();
                  date.setDate(date.getDate() + i);
                  const weekId = getWeekInfo();
                  const isPlanned = weeklyPlan[weekId].some(day =>
                    new Date(day.date).toDateString() === date.toDateString() &&
                    day.meals[selectedMealType]
                  );
                  const isSelected = selectedDates.some(d => d.toDateString() === date.toDateString());

                  return (
                    <label
                      key={i}
                      className={`relative p-3 flex flex-col items-center rounded-lg border transition-colors cursor-pointer ${isPlanned
                        ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                        : isSelected
                          ? 'border-secondary bg-secondary/5'
                          : 'border-gray-200 dark:border-gray-700 hover:border-secondary'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isPlanned}
                        onChange={() => toggleDateSelection(date)}
                        className="sr-only"
                      />
                      <div className="text-sm font-medium text-gray-800 dark:text-white">
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      {isSelected && !isPlanned && (
                        <div className="absolute top-1 right-1 text-secondary">
                          <Check size={16} />
                        </div>
                      )}
                      {isPlanned && (
                        <div className="text-xs text-secondary mt-1">Planned</div>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddToPlanModal(false);
                    setSelectedDates([]);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddToPlan}
                  disabled={selectedDates.length === 0}
                  className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add to {selectedDates.length} {selectedDates.length === 1 ? 'meal' : 'meals'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeModal;